import { useTranslation } from 'react-i18next';

import { Amount, Ltr, Pct } from '@/components/amount';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import type { KpiConfig, KpiTileSpec, TileFormat } from './types';

/**
 * One summary tile: the label in small capitals, the figure below it. The
 * two money formats go through `<Amount>` with the tile's EXPLICIT currency —
 * the hard-coded dollar the first hand-built tile carried is exactly what
 * this component exists to retire (GEN-107: a register figure is a
 * base-currency figure, and the page says which).
 */
export function KpiTile({
  label,
  value,
  format,
  currency,
  tone,
  pending = false,
}: {
  label: string;
  value: string | number | null;
  format: TileFormat;
  currency?: string | undefined;
  tone?: 'pos' | 'neg' | 'warn' | undefined;
  pending?: boolean | undefined;
}) {
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{label}</p>
        {pending ? (
          <div className="mt-1 h-8 w-24 animate-pulse rounded-md bg-muted" aria-busy />
        ) : (
          <p
            className={cn(
              'text-2xl font-bold tabular-nums',
              tone === 'pos' && 'text-emerald-700 dark:text-emerald-400',
              tone === 'neg' && 'text-red-600 dark:text-red-400',
              tone === 'warn' && 'text-amber-700 dark:text-amber-400',
            )}
          >
            <TileValue value={value} format={format} currency={currency} />
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function TileValue({
  value,
  format,
  currency,
}: {
  value: string | number | null;
  format: TileFormat;
  currency: string | undefined;
}) {
  // A read that failed draws a dash rather than a zero: zero is a claim.
  if (value === null) return <Ltr value="—" />;
  if (format === 'count') return <Ltr value={String(value)} />;
  // A percentage through the dictionary's unit (NFR-01); the trailing zeros of a 2dp string go.
  if (format === 'pct') return <Pct value={String(value).replace(/\.?0+$/, '')} />;
  // Already the page's translated text — a reason, a name; nothing to format.
  if (format === 'text') return <span className="text-lg">{String(value)}</span>;
  return <Amount value={String(value)} currency={currency} decimals={format === 'moneyWhole' ? 0 : 2} />;
}

/** The row of tiles a page's `kpis` config describes. */
export function KpiStrip<Stats>({ kpis }: { kpis: KpiConfig<Stats> }) {
  const { t } = useTranslation();
  const useStats = kpis.useStats;
  const stats = useStats();
  return (
    <div className={cn('grid gap-3', kpis.tiles.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
      {kpis.tiles.map((tile: KpiTileSpec<Stats>) => (
        <KpiTile
          key={tile.key}
          label={t(tile.labelKey)}
          value={stats.data === undefined ? null : tile.value(stats.data)}
          format={tile.format}
          currency={
            typeof tile.currency === 'function'
              ? stats.data === undefined
                ? undefined
                : tile.currency(stats.data)
              : tile.currency
          }
          tone={tile.tone}
          pending={stats.isPending && !stats.isError}
        />
      ))}
    </div>
  );
}
